import React, { FC, Suspense } from 'react';
import {
  Box,
  ButtonAppearance,
  SideSheetFooter,
  Spinner,
  SpinnerSize,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
} from '@bechtel/gds';
import {
  CommodityMilestoneActivity,
  CommodityReference,
  CommoditySection,
  CommodityStatusCode,
  CommodityWrapper,
  MaterialCommodity,
  PersonaType,
  Weld,
} from '@bechtel/types';
import { NetworkStatus, useQuery } from '@apollo/client';
import { filter, find, get, map } from 'lodash';
import { ErrorRetry, DrawerFooterButtonStyled, HistoryItem } from '@bechtel/client-common';
import { SpecificationDetailView } from '~/components/molecules/specification-detail';
import { GET_COMMODITY_DETAIL, GET_MATERIAL_COMMODITY_DETAIL_SECTIONS } from '@bechtel/client-gql';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { TabButtonStyled, TabStyled } from '~/detail/styled';
import { AssociatedWeldDetailProps } from './types';
import { CommodityTabList } from './styled';
import { t, Trans } from '@lingui/macro';
import { useWorkPackagePermissions } from '~/hooks';

const getDetailSections = (commodity: MaterialCommodity, weld: Weld) => {
  const weldTechnicalSection = find(weld.sections, (section) => section.title === 'Technicals');
  const commodityTechnicalSection = find(commodity.sections, (section) => section.title === 'Technicals');
  const commodityOtherSections = filter(commodity.sections, (section) => section.title !== 'Technicals');
  const weldDetailSections: CommoditySection[] = [];
  if (commodityTechnicalSection?.fields)
    weldDetailSections.push({
      title: `${commodity.type.name} ${t`Details`}`,
      fields: commodityTechnicalSection?.fields,
    });
  if (weldTechnicalSection?.fields)
    weldDetailSections.push({ title: t`Weld Details`, fields: weldTechnicalSection?.fields });

  return weldDetailSections.concat(...commodityOtherSections);
};

const AssociatedWeldDetail: FC<AssociatedWeldDetailProps> = ({
  selectedWeld,
  commodityId,
  showDocuments = false,
  onClaim,
}) => {
  const { tag: workPackageTag, assignedCrewMembers } = useWorkPackage();
  const { tag: commodityTag, typeName: commodityName } = useCommodity();
  const { canClaimWeld } = useWorkPackagePermissions(assignedCrewMembers);
  const { loading, error, data: weldData, refetch, networkStatus } = useQuery<
    { commodity: CommodityWrapper },
    { input: CommodityReference }
  >(GET_COMMODITY_DETAIL, {
    variables: {
      input: {
        commodityId: selectedWeld.id,
      },
    },
  });

  const {
    loading: commodityLoading,
    error: commodityError,
    data: commodityData,
    networkStatus: commodityNetworkStatus,
  } = useQuery<{ commodity: CommodityWrapper }, { input: CommodityReference }>(GET_MATERIAL_COMMODITY_DETAIL_SECTIONS, {
    variables: {
      input: {
        commodityId: commodityId,
      },
    },
  });

  const materialCommodity: MaterialCommodity = get(commodityData, 'commodity.materialCommodity');
  const weld: Weld = get(weldData, 'commodity.weld');
  const milestonesActivityHistory: CommodityMilestoneActivity[] = get(weld, 'milestonesActivityHistory.items');
  const weldSections =
    materialCommodity && materialCommodity.sections && weld && weld.sections
      ? getDetailSections(materialCommodity, weld)
      : null;
  const commodityAssignee = find(selectedWeld.commodityAssignees, (item) => {
    return item.personaType == PersonaType.FOREMAN;
  });
  const assignedWeldForeman = commodityAssignee && commodityAssignee.assignee;
  const isWeldClaimable =
    canClaimWeld &&
    assignedWeldForeman &&
    selectedWeld?.status?.status !== CommodityStatusCode.VT_APPROVED &&
    selectedWeld?.status?.status !== CommodityStatusCode.CLAIMED;
  return (
    <>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} style={{ overflowY: 'auto' }}>
          <Tabs>
            <CommodityTabList>
              <TabStyled selected={true}>
                {({ selected }: TabProps) => {
                  return (
                    <TabButtonStyled selected={!!selected}>
                      <Trans>Detail</Trans>
                    </TabButtonStyled>
                  );
                }}
              </TabStyled>
              <TabStyled>
                {({ selected }: TabProps) => {
                  return (
                    <TabButtonStyled selected={!!selected}>
                      <Trans>Activity History</Trans>
                    </TabButtonStyled>
                  );
                }}
              </TabStyled>
            </CommodityTabList>

            <TabPanels>
              <TabPanel>
                {(loading ||
                  commodityLoading ||
                  networkStatus === NetworkStatus.refetch ||
                  commodityNetworkStatus === NetworkStatus.refetch) && (
                  <Box css="height: 100%;">
                    <Box
                      flexGrow={{ sm: 1 }}
                      alignItems={{ sm: 'center' }}
                      justifyContent={{ sm: 'center' }}
                      p={{ sm: '500' }}
                      data-testid="commodity-detail-loader"
                    >
                      <Spinner size={SpinnerSize.LARGE} />
                    </Box>
                  </Box>
                )}
                {!loading && error && (
                  <Box data-testid="commodity-detail-error">
                    <ErrorRetry
                      refetch={() =>
                        refetch({
                          input: {
                            commodityId: commodityId,
                          },
                        })
                      }
                    >
                      <Trans>
                        There was a problem loading commodity details. Please click the Retry button below to try again.
                      </Trans>
                    </ErrorRetry>
                  </Box>
                )}
                {!loading && !error && (!weld || !weld.sections) && (
                  <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
                    <Trans data-testid="commodity-details-not-found">Weld details not found.</Trans>
                  </Box>
                )}
                {!loading && !commodityLoading && !error && !commodityError && weldSections && (
                  <SpecificationDetailView
                    isoDocument={weld.isoDocument}
                    commoditySections={weldSections}
                    showDocuments={showDocuments}
                    workPackageTag={workPackageTag}
                    commodityName={commodityName}
                    commodityTag={commodityTag}
                  />
                )}
              </TabPanel>
              <TabPanel>
                {!loading && error && (
                  <Box data-testid="commodity-activity-error">
                    <ErrorRetry
                      refetch={() =>
                        refetch({
                          input: {
                            commodityId: commodityId,
                          },
                        })
                      }
                    >
                      <Trans>
                        There was a problem loading commodity activity details. Please click the Retry button below to
                        try again.
                      </Trans>
                    </ErrorRetry>
                  </Box>
                )}
                {!loading && !error && (!milestonesActivityHistory || milestonesActivityHistory.length === 0) && (
                  <Box
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                    p={{ sm: '500' }}
                    data-testid="no-commodity-activity"
                  >
                    <Trans>No weld activity details found.</Trans>
                  </Box>
                )}
                {!loading && !error && milestonesActivityHistory && milestonesActivityHistory.length > 0 && (
                  <>
                    {
                      <Box alignItems={{ sm: 'center' }} flexDirection={{ sm: 'column' }}>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          mt={{ sm: '700' }}
                          data-testid="commodity-activity-history"
                        >
                          {map(milestonesActivityHistory, (activity, index: number) => (
                            <HistoryItem
                              key={activity.id}
                              {...activity}
                              last={milestonesActivityHistory.length === index + 1}
                              commodityName={commodityName}
                              workPackageTag={workPackageTag}
                              commodityTag={commodityTag}
                            />
                          ))}
                        </Box>
                      </Box>
                    }
                  </>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
        {isWeldClaimable && (
          <SideSheetFooter>
            <Box justifyContent={{ sm: 'flex-end' }}>
              <Box>
                <DrawerFooterButtonStyled
                  appearance={ButtonAppearance.SECONDARY}
                  onClick={onClaim}
                  data-testid="close-button"
                >
                  <Trans> Claim</Trans>
                </DrawerFooterButtonStyled>
              </Box>
            </Box>
          </SideSheetFooter>
        )}
      </Suspense>
    </>
  );
};

export default AssociatedWeldDetail;
